import { type Context, useContext, useMemo, useSyncExternalStore } from "react";
import { isPropertyKey, type Store } from "./common";
import {
  type BaseGetter,
  type ConsumerGetterConfig,
  type ConsumerGetterProp,
  type ConsumerGetterResult,
  type StoreValue,
} from "./types";

export function makeUseConsumerGetters<
  TStoreBaseValue,
  TConsumerGetterConfig extends ConsumerGetterConfig<TStoreBaseValue>,
>(context: Context<Store<StoreValue<TStoreBaseValue>>>, getterConfig: TConsumerGetterConfig) {
  return function useConsumerGetters<
    const TConsumerGetterProps extends ConsumerGetterProp<TStoreBaseValue, TConsumerGetterConfig>[],
  >(getterProps: TConsumerGetterProps) {
    const store = useContext(context);

    const dependencies = JSON.stringify(getterProps);
    const baseGetters = useMemo(
      () =>
        getterProps.map((getterProp) => {
          const getterCall = (
            isPropertyKey(getterProp) ? { name: getterProp, args: [] } : getterProp
          ) as Exclude<typeof getterProp, PropertyKey>;
          return (store: StoreValue<TStoreBaseValue>) =>
            getterConfig[getterCall.name](store.base, ...getterCall.args);
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [dependencies],
    );

    const results = useBaseGetters(store, baseGetters);

    return results.reduce((acc, result, ridx) => {
      const getterProp = getterProps[ridx];
      const alias = (
        isPropertyKey(getterProp) ? getterProp : (getterProp.alias ?? getterProp.name)
      ) as string;
      return { ...acc, [alias]: result };
    }, {}) as ConsumerGetterResult<TStoreBaseValue, TConsumerGetterConfig, TConsumerGetterProps>;
  };
}

export function useBaseGetters<TStoreValue, const TBaseGetters extends BaseGetter<TStoreValue>[]>(
  store: Store<TStoreValue>,
  baseGetters: TBaseGetters,
) {
  const derivedStore = useMemo(
    () =>
      store.createDerivedSnapshot(
        (value) =>
          baseGetters.map((baseGetter) => baseGetter(value)) as {
            [index in keyof TBaseGetters]: ReturnType<TBaseGetters[index]>;
          },
        (a, b) => a.length === b.length && a.every((el, idx) => Object.is(el, b[idx])),
      ),
    [store, baseGetters],
  );

  return useSyncExternalStore(
    derivedStore.subscribe,
    derivedStore.getSnapshot,
    derivedStore.getSnapshot,
  );
}
